import React, {
  useState,
  useImperativeHandle,
  forwardRef,
  useEffect,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import { Dialog, Button, useLoader } from "@a365/dsys";
import { fetchCoverages } from "@a365/api";

// Helper function to capitalize first letter
const capitalizeFirstLetter = (string) => {
  if (!string) return "";
  return string.charAt(0).toUpperCase() + string.slice(1);
};

const Coverages = forwardRef((props, ref) => {
  const { t } = useTranslation();
  const { showLoader, hideLoader } = useLoader();
  const {
    autoOpen = false,
    onCancel,
    productId = "1003546",
    productData = null,
  } = props;
  const [isDialogOpen, setIsDialogOpen] = useState(autoOpen);
  const [coveragesData, setCoveragesData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const coveragesCache = useRef(new Map());

  const openDialog = () => setIsDialogOpen(true);
  const closeDialog = () => {
    setIsDialogOpen(false);
    // Si se proporciona un callback onCancel, ejecutarlo
    if (typeof onCancel === "function") {
      onCancel({ action: "close", component: "coverages" });
    }
  };

  // Función para cargar las coberturas con debouncing y caché
  const loadCoverages = async () => {
    if (!productId) return;

    console.log("Loading coverages for productId:", productId);

    // Verificar si ya tenemos estos datos en caché
    if (coveragesCache.current.has(productId)) {
      const cachedData = coveragesCache.current.get(productId);
      setCoveragesData(cachedData);
      console.log(
        "Using cached coverages data for productId:",
        productId,
        cachedData
      );
      return;
    }

    // Show loader with backdrop and spinner
    showLoader();
    setLoading(true);
    setError(null);

    try {
      const data = await fetchCoverages(productId);

      // Guardar datos en caché
      coveragesCache.current.set(productId, data);
      setCoveragesData(data);

      // Hide loader and open dialog once data is loaded
      hideLoader();
      if (!isDialogOpen) {
        setIsDialogOpen(true);
      }
    } catch (error) {
      console.error("Error loading coverages:", error);
      setError(
        t(
          "coverages.errorLoading",
          "Error al cargar coberturas. Por favor intenta nuevamente."
        )
      );
      hideLoader();
    } finally {
      setLoading(false);
    }
  };

  // Cargar coberturas cuando se monta el componente con debouncing
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      loadCoverages();
    }, 300); // 300ms de debounce

    return () => clearTimeout(debounceTimer);
  }, [productId]);

  // Auto-abrir el dialog si se especifica la prop autoOpen
  useEffect(() => {
    if (autoOpen) {
      setIsDialogOpen(true);
    }
  }, [autoOpen]);

  // Exponer métodos públicos para controlar el componente externamente
  useImperativeHandle(ref, () => ({
    open: openDialog,
    close: closeDialog,
    isOpen: isDialogOpen,
    reload: loadCoverages,
  }));

  const handleViewDetails = () => {
    // Lógica futura para ver detalles
    console.log("Ver detalles de cobertura...");
    closeDialog();
    // También notificar al parent sobre esta acción
    if (typeof onCancel === "function") {
      onCancel({ action: "view-details", component: "coverages" });
    }
  };

  const handleCancel = () => {
    // Lógica para cancelar
    console.log("Cancelando...");
    closeDialog();
  };

  const handleRetry = () => {
    // Reintentar cargar datos
    loadCoverages();
  };

  return (
    <div data-coverages-component ref={ref}>
      <Dialog
        open={isDialogOpen}
        onClose={closeDialog}
        title={capitalizeFirstLetter(productData?.name.toLowerCase())}
        maxWidth="lg"
        footer={
          <div className="flex flex-col justify-start modal__dialog__actions w-full text-white">
            <div className="flex w-full items-center justify-between">
              <span className="text-base font-bold font-titillium capitalize">
                {productData?.name?.toLowerCase()}
              </span>
              {productData?.discount && (
                <span className="sm:hidden text-text-default font-semibold rounded-full py-[6px] px-2 bg-brand-comp-yellow block">
                  {productData?.discount} OFF
                </span>
              )}
            </div>
            <div className="flex-col flex sm:flex-row w-full">
              <div className="grid grid-cols-[200px_auto] w-full">
                <div className="data flex flex-col">
                  {productData?.installments > 1 && (
                    <span className="font-titillium text-sm font-normal">
                      x{productData?.installments} cuotas sin interes de
                    </span>
                  )}
                  <span className="text-3xl font-bold font-titillium">
                    <span>{productData?.currency_symbol}</span>{" "}
                    {productData?.installment_amount}
                  </span>
                </div>
                <div className="flex items-center">
                  <div className="flex flex-col justify-center price text-xs">
                    <span>Precio final</span>
                    <span className="line-through">
                      {productData?.currency_symbol} {productData?.basic_price}
                    </span>
                    <span className="font-bold">
                      {productData?.currency_symbol} {productData?.total_price}
                    </span>
                  </div>
                  {productData?.discount && (
                    <span className="hidden sm:block promo__discount bg-brand-comp-yellow">
                      {productData?.discount} OFF
                    </span>
                  )}
                </div>
              </div>
              <Button
                onClick={() => handleSelectProduct(idx)}
                fullWidth
                classes="mt-4 sm:w-fit sm:mt-0"
              >
                {t("select")}
              </Button>
            </div>
          </div>
        }
      >
        <div className="xdsys-space-y-4">
          {/* Estado de error */}
          {error && !loading && (
            <div className="dsys-bg-red-50 dsys-border dsys-border-red-200 dsys-text-red-700 dsys-p-4 dsys-rounded-lg">
              <div className="dsys-flex dsys-items-center">
                <span>{error}</span>
              </div>
              <div className="dsys-mt-3">
                <Button
                  variant="default"
                  color="secondary"
                  onClick={handleRetry}
                  className="dsys-text-sm dsys-py-1 dsys-px-3"
                >
                  {t("coverages.retry")}
                </Button>
              </div>
            </div>
          )}

          {/* Debug info - para identificar problemas */}
          {!loading && !error && (
            <div className="dsys-hidden">
              <p>coveragesData type: {typeof coveragesData}</p>
              <p>
                coveragesData is array:{" "}
                {Array.isArray(coveragesData) ? "true" : "false"}
              </p>
              <p>
                coveragesData length:{" "}
                {Array.isArray(coveragesData) ? coveragesData.length : "N/A"}
              </p>
              <p>coveragesData content: {JSON.stringify(coveragesData)}</p>
            </div>
          )}

          {!loading && !error && coveragesData && coveragesData.length > 0 && (
            <div className="dsys-overflow-auto dsys-max-h-[60vh]">
              <table className="dsys-w-full dsys-border-collapse">
                <tbody>
                  {coveragesData.map((coverage, index) => (
                    <tr
                      key={index}
                      className={index % 2 !== 0 ? "dsys-bg-bg-secondary" : ""}
                    >
                      <td className="dsys-text-sm py-2 dsys-text-text-default dsys-font-semibold">
                        {coverage.name}
                      </td>
                      <td className="dsys-text-right dsys-text-sm dsys-text-text-lighter dsys-font-semibold">
                        {coverage.included ? (
                          t("coverages.included", "Incluido")
                        ) : coverage.not_included ? (
                          t("coverages.notIncluded", "No incluido")
                        ) : coverage.amount ? (
                          <>
                            {coverage.currency_symbol || "USD"}{" "}
                            {coverage.amount}
                          </>
                        ) : (
                          coverage.value || ""
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Dialog>
    </div>
  );
});

export default Coverages;
